import { z } from 'zod'
import { AdditionalSupplyChainActorSchema } from 'features/declaration/additional-supply-chain-actor/models/schema'
import {
  CreateOrUpdateTransitOperationRequestAdditionalDeclarationTypeEnum,
  CreateOrUpdateTransitOperationRequestDeclarationTypeEnum,
} from 'api/models'
import {
  ActiveBorderTransportMeansSchema, AdditionalInformationSchema, AdditionalReferenceSchema,
  AuthorisationSchema,
  BooleanSchema, CountriesOfRoutingSchema,
  CustomsOfficeOfExitForTransitDeclaredSchema,
  CustomsOfficeOfTransitDeclaredSchema, DepartureTransportMeansSchema, TransportEquipmentSchema,
} from 'features/declaration/models/schemas/commonConsignmentSchemas'
import {
  CarrierSchema, ConsigneeSchema, ConsignorSchema,
  DeclarantSchema,
} from 'features/declaration/models/schemas/tradersSchema'
import LocationOfGoodsSchema from 'features/declaration/models/schemas/locationOfGoodsSchema'
import {
  PlaceOfAcceptanceSchema, PlaceOfDeliverySchema, PlaceOfLoadingSchema, PlaceOfUnloadingSchema,
} from 'features/declaration/models/schemas/placesSchemas'

import GuaranteeSchema from 'features/declaration/models/schemas/guaranteeSchema'
import {
  PreviousDocumentData,
  PreviousDocumentSchema, SupportingDocumentData, SupportingDocumentSchema, TransportDocumentData, TransportDocumentSchema,
} from 'features/declaration/models/schemas/documentSchemas'
import { AdditionalDeclarationTypeEnum, DeclarationTypeEnum, SecurityEnum, SubmitterSchema } from 'features/declaration/models/schemas/declarationEnums'

const TransitOperationFormSchema = z.object({
  id: z.number().nullable(),
  declarationType: DeclarationTypeEnum.default(CreateOrUpdateTransitOperationRequestDeclarationTypeEnum.T1),
  deleted: z.boolean().optional(),
  status: z.string().default('DRAFT').nullable(),
  mrn: z.string().nullable(),
  ensMrn: z.string().nullable(),
  security: SecurityEnum,
  limitDate: z.string().nullable(), // expected date of arrival at destination
  reducedDatasetIndicator: BooleanSchema.default('false'),
  bindingItinerary: BooleanSchema.default('false'),
  additionalDeclarationType: AdditionalDeclarationTypeEnum.default(CreateOrUpdateTransitOperationRequestAdditionalDeclarationTypeEnum.A),
  lrn: z.string().nullable(),
  countryOfDispatch: z.string(),
  countryOfDestination: z.string(),
  containerIndicator: BooleanSchema.default('false'),
  entryIndicator: BooleanSchema.default('false'),
  inlandModeOfTransport: z.string(),
  borderModeOfTransport: z.string(),
  grossMass: z.number().min(0).refine((arg) => /^\d{1,11}(\.\d{1,3})?$/.exec(String(arg))),
  transportChargesMethodOfPayment: z.string().nullable(),
  referenceNumberUCR: z.string().nullable(),
  departureCustomsOffice: z.string(),
  destinationCustomsOffice: z.string().nullable(),
  consignmentId: z.number().nullable(),
  customerId: z.number().nullable(),

  carrier: CarrierSchema,
  consignor: ConsignorSchema,
  consignee: ConsigneeSchema,
  declarant: DeclarantSchema.nullable(),
  locationOfGoods: LocationOfGoodsSchema.nullable(),
  placeOfLoading: PlaceOfLoadingSchema,
  placeOfUnloading: PlaceOfUnloadingSchema,
  placeOfAcceptance: PlaceOfAcceptanceSchema,
  placeOfDelivery: PlaceOfDeliverySchema,

  transitCustomsOffices: z.array(CustomsOfficeOfTransitDeclaredSchema),
  customsOfficesOfTransitExit: z.array(CustomsOfficeOfExitForTransitDeclaredSchema),
  guarantee: GuaranteeSchema,
  authorisations: z.array(AuthorisationSchema),
  additionalSupplyChainActor: z.array(AdditionalSupplyChainActorSchema),
  transportEquipment: z.array(TransportEquipmentSchema),
  departureTransportMeans: z.array(DepartureTransportMeansSchema),
  countryOfRoutingOfConsignment: z.array(CountriesOfRoutingSchema),
  activeBorderTransportMeans: z.array(ActiveBorderTransportMeansSchema),
  previousDocument: z.array(PreviousDocumentSchema),
  supportingDocument: z.array(SupportingDocumentSchema),
  transportDocument: z.array(TransportDocumentSchema),
  additionalReference: z.array(AdditionalReferenceSchema),
  additionalInformation: z.array(AdditionalInformationSchema),
  specificCircumstanceIndicator: z.string(),
  submitter: SubmitterSchema.optional(),
})

export type TransitOperationFormValues = z.infer<typeof TransitOperationFormSchema>

export type TransitOperationData =
  Omit<TransitOperationFormValues, 'transportDocument' | 'previousDocument' | 'supportingDocument'> & {
    transportDocument: TransportDocumentData[]
    previousDocument: PreviousDocumentData[]
    supportingDocument: SupportingDocumentData[]
  }

export default TransitOperationFormSchema
